// Library methods
import { useCallback, useState } from "react";
import { useTranslation } from "react-i18next";

// Components
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Grid from "@mui/material/Grid";
import Typography from "@mui/material/Typography";
import DefaultModal from "src/components/shared/modals/DefaultModal/DefaultModal";
import TextInputField from "src/components/shared/inputs/TextInputField/TextInputField";

// Hooks
import { useCreateEMRHub } from "src/services/emr/hooks/emr-hubs/useCreateEMRHub";

// Styles
import {
  getRequiredErrorColor,
  ModalCustomCancel,
  ModalCustomConfirm,
} from "src/styles/mui/muiStylesHelper";
import { useTheme } from "@mui/material/styles";

type AddEMRHubModalType = {
  open: boolean;
  clinicId: string | undefined;
  onClose: () => void;
};

const AddEMRHubModal = ({ open, clinicId, onClose }: AddEMRHubModalType) => {
  const { t } = useTranslation();
  const theme = useTheme();

  const [alias, setAlias] = useState("");

  const { mutate, isPending } = useCreateEMRHub(clinicId);

  const handleClose = useCallback(() => {
    setAlias("");
    onClose();
  }, [onClose]);

  const handleChange = useCallback(
    (event: React.ChangeEvent<HTMLTextAreaElement | HTMLInputElement>) => {
      setAlias(event.target.value);
    },
    []
  );

  const handleConfirm = useCallback(() => {
    mutate(
      { alias },
      {
        onSuccess: () => {
          setAlias("");
          onClose();
        },
      }
    );
  }, [mutate, alias, onClose]);

  return (
    <DefaultModal
      isOpen={open}
      onClose={handleClose}
      title={t("Add EMR Hub")}
      maxWidth="sm"
      modalActions={
        <Grid sx={{ display: "flex", justifyContent: "flex-end", gap: "8px" }}>
          <Button sx={ModalCustomCancel} onClick={handleClose}>
            {t("Cancel")}
          </Button>
          <Button
            sx={ModalCustomConfirm}
            onClick={handleConfirm}
            disabled={!alias.trim() || isPending}
          >
            {t("Add")}
          </Button>
        </Grid>
      }
    >
      <Grid display="flex" flexDirection="column" p="16px" gap="8px">
        <Box display="flex" flexDirection="column" gap="4px">
          <Typography fontSize="12px">
            {t("Alias")}
            <span style={{ color: getRequiredErrorColor(theme) }}>*</span>
          </Typography>
          <TextInputField
            id="add-emr-hub-alias-input"
            autoFocus
            required
            placeholder={t("Enter a value")}
            value={alias}
            onChange={handleChange}
            maxLength={50}
          />
        </Box>
      </Grid>
    </DefaultModal>
  );
};

export default AddEMRHubModal;
